/**
 * Token storage split across two scopes so different browser tabs can hold
 * different businesses while staying logged in across refresh and browser
 * restart:
 *
 * - Identity token (`localStorage`): shared across all tabs, survives a browser
 *   close/reopen. Changes only on login/logout. Acts as the "who am I" seed and
 *   the default a fresh tab falls back to.
 * - Tab token (`sessionStorage`): this tab's pinned business-scoped token.
 *   Tab-scoped, so two tabs can diverge; survives refresh and Chrome session
 *   restore. Absent until a tab pins a business (Feature 2 bootstrap/switch).
 *
 * `getActiveToken()` is the single source every request and decode reads from:
 * the tab's own token if it has pinned one, else the shared identity token. When
 * no tab has pinned a token (today's behaviour) it always returns the identity
 * token, so this layer is a no-op until per-tab business is wired up.
 */

// Same key name in both scopes on purpose — sessionStorage and localStorage are
// separate namespaces, so a tab's `_UFT_` never collides with the shared one,
// and the token always lives under the familiar `_UFT_` key.
import jwtDecode from 'jwt-decode';

import { isEmbedPreviewSearch } from 'utils/embedBoot';

const IDENTITY_TOKEN_KEY = '_UFT_';
const TAB_TOKEN_KEY = '_UFT_';
const EMBED_PREVIEW_TAB_TOKEN_KEY = '_UFT_preview';
const TAB_ID_KEY = '_heltar_tab_id';

const tabTokenKey = (): string =>
  isEmbedPreviewSearch(window.location.search)
    ? EMBED_PREVIEW_TAB_TOKEN_KEY
    : TAB_TOKEN_KEY;

export const getIdentityToken = (): string | null =>
  localStorage.getItem(IDENTITY_TOKEN_KEY);

export const setIdentityToken = (token: string): void =>
  localStorage.setItem(IDENTITY_TOKEN_KEY, token);

export const clearIdentityToken = (): void =>
  localStorage.removeItem(IDENTITY_TOKEN_KEY);

export const getTabToken = (): string | null =>
  sessionStorage.getItem(tabTokenKey());

export const setTabToken = (token: string): void =>
  sessionStorage.setItem(tabTokenKey(), token);

export const clearTabToken = (): void =>
  sessionStorage.removeItem(tabTokenKey());

/** The token this tab authenticates and decodes with. */
export const getActiveToken = (): string | null =>
  getTabToken() ?? getIdentityToken();

/**
 * Make this tab's business the default a fresh tab adopts: copy the tab's token
 * into the shared identity (`localStorage`). Called while this tab is the
 * visible/active one, so a new tab — which falls back to the identity token —
 * and the next login open the business the user was last looking at. No-op when
 * the tab has no token of its own, or the identity already matches (so it never
 * spuriously wakes other tabs through the `storage` event).
 */
export const adoptTabTokenAsIdentity = (): void => {
  const tabToken = getTabToken();
  if (tabToken && tabToken !== getIdentityToken()) setIdentityToken(tabToken);
};

/** Clear every token for this tab — used on logout. */
export const clearAllTokens = (): void => {
  clearTabToken();
  clearIdentityToken();
  // Don't let the in-dashboard "test widget" flag (TestWidgetHost) outlive the
  // session — otherwise on a shared machine the next admin would see it auto-
  // mount. Plain key (no import) to avoid coupling this util to the component.
  try {
    localStorage.removeItem('heltar:test-widget');
  } catch {
    /* storage unavailable — nothing to clear */
  }
};

/** The business id encoded in a token, or null if absent/undecodable. */
export const businessIdFromToken = (token: string | null): number | null => {
  if (!token) return null;
  try {
    const decoded = jwtDecode(token.replace('Bearer ', '')) as {
      business?: { id?: number };
    };
    return decoded?.business?.id ?? null;
  } catch {
    return null;
  }
};

/** Stable per-tab id, created once and kept for the life of the tab. */
export const getTabId = (): string => {
  let id = sessionStorage.getItem(TAB_ID_KEY);
  if (!id) {
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(TAB_ID_KEY, id);
  }
  return id;
};

/** Replace this tab's id — used when a duplicated tab inherits another's id. */
export const resetTabId = (): string => {
  sessionStorage.removeItem(TAB_ID_KEY);
  return getTabId();
};
